import React, { useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { v4 as uuidv4 } from "uuid";

import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import AddIcon from "@material-ui/icons/Add";
import { Campaign as CampaignIcon, PersonSearch as PersonSearchIcon } from "@mui/icons-material";

import { AuthContext } from "../../../context/Auth/AuthContext";
import { TicketsContext } from "../../../context/Tickets/TicketsContext";
import useTickets from "../../../hooks/useTickets";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import NewTicketModal from "../../../components/NewTicketModal";

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const MetricCard = ({ label, value, hint, tokens }) => (
  <div style={{ padding: 18, borderRadius: 14, background: tokens.surfaceSoft, display: "flex", flexDirection: "column", gap: 6 }}>
    <span style={{ fontSize: 13, color: tokens.textMuted }}>{label}</span>
    <span style={{ fontSize: 30, fontWeight: 700, color: tokens.text }}>{value}</span>
    <span style={{ fontSize: 12, color: tokens.textMuted }}>{hint}</span>
  </div>
);

const ShortcutButton = ({ icon, label, bg, fg, onClick, tokens }) => (
  <button
    onClick={onClick}
    style={{
      height: 56,
      padding: "0 16px",
      border: `1px solid ${tokens.border}`,
      borderRadius: 12,
      background: tokens.surface,
      display: "flex",
      alignItems: "center",
      gap: 12,
      fontSize: 14,
      fontWeight: 500,
      color: tokens.text,
      textAlign: "left",
      cursor: "pointer",
    }}
  >
    <span style={{ width: 32, height: 32, borderRadius: 8, background: bg, color: fg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {icon}
    </span>
    {label}
  </button>
);

// Tela "Visao do dia" (Atendimentos sem ticket aberto) - projeto
// 2026-10-21, ver PROMPT.md em D:\voicechat\novo layout. So' usa dados
// reais via useTickets (mesmo hook do painel de lista e do resto do
// app) - "Tempo medio de resposta" fica "-" porque nao ha' hoje uma
// metrica pronta pra isso fora do Dashboard (ver tarefa #62).
const VisaoDoDia = ({ tokens, onSearchContact }) => {
  const { user } = useContext(AuthContext);
  const { setCurrentTicket } = useContext(TicketsContext);
  const history = useHistory();
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const today = format(new Date(), "yyyy-MM-dd");
  const myQueueIds = useMemo(() => (user.queues || []).map((q) => q.id), [user.queues]);
  // Admin ve' a equipe toda (pedido do usuario 2026-10-21: "precisa
  // deixar para os adm conseguir ver todos") - mesmo sinal que o resto
  // do sistema ja usa (TicketsManagerTabs no Classico).
  const showAllTickets = user.profile === "admin" || user.allUserChat === "enabled";

  const { tickets: myOpenTickets } = useTickets({ status: "open", showAll: showAllTickets ? "true" : "false" });
  // O backend ja' devolve so' os tickets certos (pessoais ou da equipe
  // toda, conforme showAll) - nao precisa filtrar de novo aqui.
  const mine = myOpenTickets || [];
  const unreadCount = mine.reduce((acc, t) => acc + (Number(t.unreadMessages) || 0), 0);

  const { count: closedTodayCount } = useTickets({
    status: "closed",
    dateStart: today,
    dateEnd: today,
    showAll: showAllTickets ? "true" : "false",
  });

  const { tickets: pendingTickets } = useTickets({
    status: "pending",
    showAll: showAllTickets ? "true" : "false",
    queueIds: !showAllTickets && myQueueIds.length ? JSON.stringify(myQueueIds) : undefined,
  });
  const needsAttention = useMemo(
    () => [...(pendingTickets || [])].sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt)).slice(0, 5),
    [pendingTickets]
  );

  const queueLabel = mine[0]?.queue?.name ? `na fila ${mine[0].queue.name}` : mine.length ? "em atendimento" : "—";

  const handleRespond = async (ticket) => {
    try {
      if (!ticket.userId) {
        await api.put(`/tickets/${ticket.id}`, {
          status: ticket.isGroup ? "group" : "open",
          userId: user.id,
        });
      }
      setCurrentTicket({ id: ticket.id, uuid: ticket.uuid, code: uuidv4() });
    } catch (err) {
      toastError(err);
    }
  };

  const handlePickNext = async () => {
    if (needsAttention.length === 0) return;
    setClaiming(true);
    try {
      await handleRespond(needsAttention[0]);
    } finally {
      setClaiming(false);
    }
  };

  return (
    <section
      aria-label="Visão do dia"
      style={{
        flex: "999 1 480px",
        minWidth: 0,
        background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: 16,
        padding: 32,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 28,
        overflowY: "auto",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 13, color: tokens.textMuted }}>
            {capitalize(format(new Date(), "EEEE, d 'de' MMMM", { locale: ptBR }))}
          </span>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: "-0.01em", color: tokens.text }}>Visão do dia</h1>
        </div>
        <button
          onClick={handlePickNext}
          disabled={claiming || needsAttention.length === 0}
          style={{
            height: 40,
            padding: "0 16px",
            border: "none",
            borderRadius: 10,
            background: tokens.primary,
            color: "#FFFFFF",
            fontSize: 14,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: needsAttention.length === 0 ? "default" : "pointer",
            opacity: needsAttention.length === 0 ? 0.5 : 1,
          }}
        >
          <AssignmentTurnedInIcon fontSize="small" /> Pegar próximo da fila
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <MetricCard tokens={tokens} label="Em atendimento" value={mine.length} hint={queueLabel} />
        <MetricCard tokens={tokens} label="Mensagens não lidas" value={unreadCount} hint={`em ${mine.length} conversas`} />
        <MetricCard tokens={tokens} label="Tempo médio de resposta" value="—" hint="calculado ao longo do dia" />
        <MetricCard tokens={tokens} label="Encerrados hoje" value={closedTodayCount ?? "—"} hint="atualiza ao fechar tickets" />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: tokens.text }}>Precisam de atenção</h2>
        {needsAttention.length === 0 ? (
          <div style={{ fontSize: 13, color: tokens.textMuted, padding: "8px 0" }}>Nada pendente por aqui 🎉</div>
        ) : (
          <div style={{ border: `1px solid ${tokens.divider}`, borderRadius: 14, overflow: "hidden" }}>
            {needsAttention.map((ticket, idx) => {
              const waitMinutes = ticket.updatedAt ? Math.floor((Date.now() - new Date(ticket.updatedAt).getTime()) / 60000) : 0;
              const isLate = waitMinutes >= 5;
              return (
                <div
                  key={ticket.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 16px",
                    borderBottom: idx < needsAttention.length - 1 ? `1px solid ${tokens.divider}` : "none",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: "0 0 36px", height: 36, borderRadius: 18, background: tokens.primarySoft, color: tokens.primaryText, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 13 }}>
                    {(ticket.contact?.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 180, display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: tokens.text }}>{ticket.contact?.name}</span>
                    <span style={{ fontSize: 13, color: tokens.textMuted }}>
                      Sem resposta há {waitMinutes} min · {ticket.unreadMessages || 0} mensagens novas
                    </span>
                  </div>
                  {isLate && (
                    <span style={{ height: 24, padding: "0 10px", borderRadius: 12, background: "#FFF1E6", color: "#9A3F07", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center" }}>
                      Atrasando
                    </span>
                  )}
                  <button
                    onClick={() => handleRespond(ticket)}
                    style={{ height: 36, padding: "0 14px", borderRadius: 10, border: `1px solid ${tokens.primarySofterBorder}`, background: tokens.primarySofter, color: tokens.primaryText, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                  >
                    Responder →
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: tokens.text }}>Atalhos</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <ShortcutButton tokens={tokens} icon={<AddIcon fontSize="small" />} bg={tokens.primarySoft} fg={tokens.primaryText} label="Novo ticket" onClick={() => setNewTicketOpen(true)} />
          <ShortcutButton tokens={tokens} icon={<PersonSearchIcon fontSize="small" />} bg="#E8EEFF" fg="#1E46A8" label="Buscar contato" onClick={() => history.push("/contacts")} />
          <ShortcutButton tokens={tokens} icon={<CampaignIcon fontSize="small" />} bg="#FFF1E6" fg="#9A3F07" label="Enviar campanha" onClick={() => history.push("/campaigns")} />
        </div>
      </div>

      <NewTicketModal
        modalOpen={newTicketOpen}
        onClose={(ticket) => {
          setNewTicketOpen(false);
          if (ticket) setCurrentTicket({ id: ticket.id, uuid: ticket.uuid, code: uuidv4() });
        }}
      />
    </section>
  );
};

export default VisaoDoDia;
